/**
 * Mapping helpers that turn the API `NotificationView` into the row VM the
 * popup renders. Kept in a separate file so the popup component stays
 * presentational and the mapping rules (tone selection, relative-time
 * formatting, unread derivation, actor avatar + click target) are easy
 * to find / test later.
 */
import { urlForBlock, urlForCollection, urlForGroup } from "@shared/types/urls";
import { resolveAttributionIdentity, } from "@/components/block/lip-attribution";
import { formatRelativeTime } from "@/lib/format-relative-time";
// ---------------------------------------------------------------------------
// Tone
// ---------------------------------------------------------------------------
// Event-type prefixes that should render with the attention-getting "warn"
// tone. Reminder events (renderer pulls them when a reminder fires) and any
// future `*.deadline` flavor surface as warn; everything else falls back to
// the neutral "info" tone. Matching is prefix-based so namespaced sub-events
// like `reminder.due.soft` collapse to the same bucket as `reminder.due`.
const WARN_EVENT_PREFIXES = ["reminder.", "deadline."];
function deriveTone(eventType) {
    if (!eventType)
        return "info";
    return WARN_EVENT_PREFIXES.some((p) => eventType.startsWith(p)) ? "warn" : "info";
}
/**
 * The API's `NotifierService` packs an iMessage-friendly deep link into
 * `body` (`https://putty.me/g/...` for collection-scoped events,
 * `https://putty.me/b/...` for block-annotated events) so SMS/iMessage
 * recipients get a tap-target. That URL is noise in the in-app popup —
 * the row already gives the user context via `label`, and the popup's
 * click handler resolves navigation from `clientPayload`, not from
 * parsing `body`. Treat any body that's a bare `http(s)://...` URL as
 * "no sublabel" so the row renders as a single clean line. Trailing
 * whitespace is tolerated; embedded text after the URL still shows (we
 * only collapse the pure-URL case).
 */
function isBareUrl(s) {
    return /^https?:\/\/\S+\s*$/.test(s);
}
// ---------------------------------------------------------------------------
// Actor avatar
// ---------------------------------------------------------------------------
/**
 * Resolve an actor's avatar + initial from the notification's
 * `clientPayload`. The notifier stashes both `actorId` (always) and the
 * optional `actorName` / `actorEmail` on every event payload (see
 * `production/api/src/services/notifier/service.ts`'s `resolveActorIdentity`
 * helper), and we feed all three into the same lookup pipeline the lip
 * uses so avatars stay consistent across surfaces.
 *
 * Implementation: synthesize a single-member `MemberForLipAttribution`
 * record and hand it to `resolveAttributionIdentity`. The helper's
 * lookup ladder (email → team palette → initial fallback) is exactly
 * what we want; no avatar logic gets duplicated here.
 *
 * Falls back to a sensible "?" identity when `actorId` is missing — admin
 * sends and any future actor-less event surface this. The popup paints
 * the initial as text in that case.
 */
function resolveActorAvatar(clientPayload) {
    const actorId = readString(clientPayload?.actorId);
    if (actorId === undefined)
        return { initial: "?" };
    const actorName = readString(clientPayload?.actorName);
    const actorEmail = readString(clientPayload?.actorEmail);
    // `clientPayload` is JSONB so `actorId` arrives as a plain string from
    // the wire — re-brand it as `AccountId` here. The lip-attribution
    // helper only ever compares strings under the hood, so this is a pure
    // type assertion (no runtime work).
    const brandedActorId = actorId;
    const synthesizedMember = {
        accountId: brandedActorId,
        email: actorEmail,
        username: actorName,
    };
    return resolveAttributionIdentity(brandedActorId, [synthesizedMember]);
}
// ---------------------------------------------------------------------------
// Click target — eventType + clientPayload → in-app path
// ---------------------------------------------------------------------------
/**
 * Map a notification to the in-app surface the user expects when they
 * click its row. Each event type carries the entity ids it needs on
 * `clientPayload` (set by `NotifierService` at enqueue time); this
 * function picks the matching URL builder.
 *
 * Routing rules (mirrors the body URL the notifier picks, just as a
 * relative path so `useNavigate()` doesn't trigger a full page load):
 *
 *   - Collection-scoped events → `urlForCollection` (slug-based URL when
 *     `collectionSlug` + parent context are on the payload, falling back
 *     to `/g/{collectionId}`).
 *   - `group.member_added` → `urlForGroup` (slug-based when the group
 *     has a public slug and isn't implicit, else `/g/{groupId}`).
 *   - `block.annotated` → `/b/{blockSlug}` when present, else the
 *     containing collection's URL via the same `urlForCollection`
 *     fallback.
 *
 * The fallback to `/g/{id}` matters when the row was enqueued before
 * the slug-component plumbing landed (existing pending queue rows have
 * no slug fields on `clientPayload`). Once delivered, those rows still
 * navigate cleanly — and the viewer auto-upgrades the visible URL to
 * the slug form once the collection lands in cache.
 *
 * Returns `undefined` for unknown event types (admin sends, future
 * events) — the popup renders the row as non-clickable in that case.
 */
function deriveTargetPath(eventType, clientPayload) {
    if (clientPayload === undefined)
        return undefined;
    switch (eventType) {
        case "collection.member_added": {
            const collectionId = readString(clientPayload.collectionId);
            if (collectionId === undefined)
                return undefined;
            return urlForCollectionFromPayload(clientPayload, collectionId);
        }
        case "group.member_added": {
            const groupId = readString(clientPayload.groupId);
            if (groupId === undefined)
                return undefined;
            return urlForGroup({
                id: groupId,
                slug: readString(clientPayload.groupSlug),
                isImplicit: clientPayload.groupIsImplicit === true,
            });
        }
        case "collection.created_in_group": {
            const collectionId = readString(clientPayload.collectionId);
            if (collectionId === undefined)
                return undefined;
            return urlForCollectionFromPayload(clientPayload, collectionId);
        }
        case "block.added_to_collection": {
            const collectionId = readString(clientPayload.collectionId);
            if (collectionId === undefined)
                return undefined;
            return urlForCollectionFromPayload(clientPayload, collectionId);
        }
        case "block.annotated": {
            const blockSlug = readString(clientPayload.blockSlug);
            const blockUrl = urlForBlock({ slug: blockSlug });
            if (blockUrl !== undefined)
                return blockUrl;
            const blockCollectionId = readString(clientPayload.blockCollectionId);
            if (blockCollectionId === undefined)
                return undefined;
            return urlForCollection({
                id: blockCollectionId,
                slug: readString(clientPayload.blockCollectionSlug),
                parentCollectionId: readString(clientPayload.blockCollectionParentId),
            }, { parentSlug: readString(clientPayload.blockCollectionParentSlug) });
        }
        default:
            return undefined;
    }
}
/**
 * Read the standard collection slug-component fields off `clientPayload`
 * and feed them to `urlForCollection`. Shared by every collection-scoped
 * event since the field names are identical
 * (`collectionSlug` / `collectionParentId` / `collectionParentSlug`).
 * Falls back to `/g/{id}` when slug data is missing.
 */
function urlForCollectionFromPayload(clientPayload, collectionId) {
    return urlForCollection({
        id: collectionId,
        slug: readString(clientPayload.collectionSlug),
        parentCollectionId: readString(clientPayload.collectionParentId),
    }, { parentSlug: readString(clientPayload.collectionParentSlug) });
}
export function toNotificationItemVM(n, opts = {}) {
    const showBodyAsSublabel = n.body.length > 0 && isBareUrl(n.body) === false;
    return {
        id: n.id,
        timeLabel: formatRelativeTime(n.createdAt, opts.nowMs),
        label: n.title,
        sublabel: showBodyAsSublabel ? n.body : undefined,
        tone: deriveTone(n.eventType),
        // Server-computed: `NotificationView.isRead` is derived from
        // `Account.notificationsViewedAt` by the route layer, so read/unread
        // stays consistent across devices without the viewer maintaining its
        // own cursor.
        isRead: n.isRead,
        avatar: resolveActorAvatar(n.clientPayload),
        targetPath: deriveTargetPath(n.eventType, n.clientPayload),
    };
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/**
 * Narrow an unknown `clientPayload` field to a non-empty string. The
 * payload is typed as `Record<string, unknown>` end-to-end so every
 * read goes through this — malformed rows (a missing field, the wrong
 * shape from a future event type) degrade gracefully into undefined
 * instead of throwing during render.
 */
function readString(value) {
    if (typeof value !== "string")
        return undefined;
    if (value.length === 0)
        return undefined;
    return value;
}
